import type { AuthenticationContext } from '@xpanse/authz-service-client';

import { ErrorAlert } from '@/components';
import Label from '@/components/label';
import { useAppContext } from '@/context/AppContext';
import RoleGuard from '@/context/auth/RoleGuard';
import { CustomerType } from '@/enums';
import { useGetAuthenticationContexts, useUserProfile } from '@/hooks/useUsers';
import { useXpanseAuth } from '@/hooks/useXpanseAuthentication';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

const filterOptions = createFilterOptions<AuthenticationContext>({
  stringify: (option) =>
    `${option.organizationName} ${option.organizationCode} ${option.organizationClientCode}`,
});

const AuthContextMenuButton = ({ authContexts }: { authContexts: AuthenticationContext[] }) => {
  const { getAccessTokenSilently } = useXpanseAuth();
  const { isLoadingAuthContext } = useAppContext();
  const navigate = useNavigate();
  const { organizationCode } = useUserProfile();
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const currentAuthContext = authContexts.find(
    (authContext) => authContext.organizationCode === organizationCode
  );

  const handleChangeAuthContext = useCallback(
    async (event: any, newValue: AuthenticationContext | null) => {
      if (!newValue || newValue.organizationCode === currentAuthContext?.organizationCode) {
        return;
      }
      isLoadingAuthContext.onTrue();

      await getAccessTokenSilently({
        authorizationParams: { scope: `refresh ${newValue.scope}` },
        detailedResponse: true,
        cacheMode: 'off',
      });

      isLoadingAuthContext.onFalse();
      navigate('/');
      window.location.reload();
    },
    [getAccessTokenSilently, navigate, isLoadingAuthContext, currentAuthContext]
  );

  if (authContexts.length === 1) {
    return null;
  }

  const SHOW_SEARCH_THRESHOLD = 10;
  const options = authContexts ?? [];
  const enableSearch = options.length >= SHOW_SEARCH_THRESHOLD;

  return (
    <Autocomplete
      className="large-autocomplete"
      size="small"
      options={options}
      getOptionLabel={(option) => option.organizationName}
      filterOptions={enableSearch ? filterOptions : (x) => x}
      value={currentAuthContext || undefined}
      onChange={handleChangeAuthContext}
      openOnFocus={enableSearch}
      inputValue={enableSearch ? inputValue : undefined}
      onInputChange={(_, newInputValue) => {
        if (enableSearch) {
          setInputValue(newInputValue);
        }
      }}
      ListboxProps={{ style: { maxHeight: 480, overflowY: 'auto' } }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" sx={{ minWidth: 300 }}
          placeholder={enableSearch ? 'Search organization' : 'Select organization'}
          onFocus={() => {
            if (enableSearch) {
              setIsFocused(true);
              setInputValue('');
            }
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          InputProps={{ ...params.InputProps,
            startAdornment: currentAuthContext && !(enableSearch && isFocused) ? (
              <>
                <InputAdornment position="start">
                  <Label color="primary" sx={{ mr: 1 }}>
                    <span>{currentAuthContext.organizationClientCode}</span>
                  </Label>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
          inputProps={{ ...params.inputProps, readOnly: !enableSearch }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.scope}>
          <Label color="primary"><span>{option.organizationClientCode}</span></Label>
          <Box component="span" sx={{ flexGrow: 1, textAlign: 'right' }}>
            {option.organizationName}
          </Box>
        </li>
      )}
      sx={{ minWidth: 240 }}
      isOptionEqualToValue={(option, value) =>
        option.organizationCode === value.organizationCode
      }
      disableClearable
    />
  );
};

export default function AuthContextMenu() {
  const {
    data: authContexts,
    error,
    isLoading: isFetchingAuthContexts,
    isSuccess,
  } = useGetAuthenticationContexts();
  const { user } = useXpanseAuth();

  const isLoading = isFetchingAuthContexts || !user;

  return (
    <RoleGuard role={CustomerType.lender}>
      {error && <ErrorAlert error={error} />}
      {isLoading && <CircularProgress color="inherit" size="24px" />}
      {isSuccess && user && <AuthContextMenuButton authContexts={authContexts} />}
    </RoleGuard>
  );
}
